import React, { useRef, useCallback, useState } from "react";
import styled, { css } from "styled-components";
import { sendPost } from "../api/post";
import { breakpoints } from "../styles/theme";
import { placeholderTexts } from "../constants/placeholderTexts";

interface PostInputProps {
  inModal?: boolean;
}

const PostInput: React.FC<PostInputProps> = ({ inModal = false }) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [content, setContent] = useState("");
  const [isPosting, setIsPosting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [placeholder] = useState(
    placeholderTexts[Math.floor(Math.random() * placeholderTexts.length)]
  );

  const handleInput = useCallback(
    (event: React.ChangeEvent<HTMLTextAreaElement>) => {
      const textarea = event.target;
      textarea.style.height = "auto";
      textarea.style.height = `${textarea.scrollHeight}px`;
      setContent(textarea.value);
    },
    []
  );

  const handlePost = async () => {
    if (!content.trim() || isPosting) return;

    if (content.length > 200) {
      alert("投稿は200文字以内にしてください。");
      return;
    }

    setIsPosting(true);
    setError(null);
    try {
      await sendPost(content);
      setContent("");
      if (textareaRef.current) {
        textareaRef.current.value = "";
        textareaRef.current.style.height = "auto";
      }
      window.location.reload();
    } catch (err) {
      console.error(err);
      alert("投稿に失敗しました。もう一度お試しください。");
    } finally {
      setIsPosting(false);
    }
  };

  const handleCancel = () => {
    setContent("");
    if (textareaRef.current) {
      textareaRef.current.value = "";
      textareaRef.current.style.height = "auto";
    }
  };

  return (
    <Container $inModal={inModal}>
      <InputBox>
        <TextArea
          ref={textareaRef}
          placeholder={placeholder}
          rows={1}
          onChange={handleInput}
          value={content}
          disabled={isPosting}
        />
        {error && <ErrorText>{error}</ErrorText>}
        <Actions>
          <Buttons>
            <Button
              onClick={handlePost}
              disabled={isPosting || !content.trim()}
              primary
            >
              {isPosting ? "投稿中..." : "投稿"}
            </Button>
            <Button onClick={handleCancel} disabled={isPosting}>
              キャンセル
            </Button>
          </Buttons>
          <Counter isError={content.length > 200}>{content.length}/200</Counter>
        </Actions>
      </InputBox>
    </Container>
  );
};

const Container = styled.div<{ $inModal: boolean }>`
  position: relative;
  margin-bottom: 30px;
  max-width: 900px;
  width: 100%;
  margin: 10px auto 30px;
  box-sizing: border-box;

  ${({ $inModal }) =>
    !$inModal &&
    css`
      @media (max-width: ${breakpoints.mobile}) {
        display: none;
      }
    `}
`;

const InputBox = styled.div`
  background: #f5f5f5;
  border-radius: 24px;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  position: relative;
`;

const TextArea = styled.textarea`
  background: transparent;
  border: none;
  outline: none;
  resize: none;
  color: rgb(39, 39, 39);
  font-size: 16px;
  width: 100%;
  box-sizing: border-box;
  min-height: 80px;
  max-height: 200px;
`;

const Actions = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Counter = styled.span<{ isError: boolean }>`
  font-size: 12px;
  font-weight: 500;
  color: ${({ isError }) => (isError ? "#e74c3c" : "#999")};
`;

const Buttons = styled.div`
  display: flex;
  gap: 12px;
`;

const Button = styled.button<{ primary?: boolean }>`
  border-radius: 20px;
  padding: 8px 20px;
  font-weight: 600;
  cursor: pointer;
  border: none;
  font-size: 1rem;
  border: 1px solid #dddddd;
  background: ${({ primary }) => (primary ? "#4a4a4a" : "#ffffff")};
  color: ${({ primary }) => (primary ? "#ffffff" : "#4a4a4a")};

  &:disabled {
    cursor: not-allowed;
    background: ${({ primary }) => (primary ? "#777777" : "#f5f5f5")};
    color: ${({ primary }) => (primary ? "#dddddd" : "#aaaaaa")};
  }
  &:hover {
    box-shadow: 0 5px 10px rgba(0, 0, 0, 0.05); // ← ホバー時の影
  }
`;

const ErrorText = styled.p`
  color: #e74c3c;
`;

export default PostInput;
